// [ACTIVITY-LOG] Journal d'activité de la boutique
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Loader2, ShoppingBag, Tag, Trash2, Pencil, PlusCircle, ShoppingCart, PackagePlus, Users, Shield, RefreshCw, Filter } from "lucide-react";

const ACTION_META = {
  create:         { label: "Création",       Icon: PlusCircle,   color: "#22C55E" },
  update:         { label: "Modification",   Icon: Pencil,       color: "#3B82F6" },
  delete:         { label: "Suppression",    Icon: Trash2,       color: "#EF4444" },
  sale:           { label: "Vente",          Icon: ShoppingCart, color: "#F59E0B" },
  restock:        { label: "Réappro. stock", Icon: PackagePlus,  color: "#10B981" },
  team_create:    { label: "Nouveau membre", Icon: Users,        color: "#8B5CF6" },
  team_update:    { label: "Rôle modifié",   Icon: Users,        color: "#8B5CF6" },
  team_delete:    { label: "Membre supprimé",Icon: Users,        color: "#EF4444" },
  invoice_create: { label: "Facture",        Icon: ShoppingCart, color: "#F59E0B" },
  pin_set:        { label: "PIN configuré",  Icon: Shield,       color: "#0EA5E9" },
  pin_change:     { label: "PIN modifié",    Icon: Shield,       color: "#0EA5E9" },
  pin_remove:     { label: "PIN retiré",     Icon: Shield,       color: "#EF4444" },
};

const ENTITY_LABEL = {
  product: "Article", category: "Catégorie", variant: "Variante",
  invoice: "Facture", sale: "Vente", team_member: "Équipe",
  settings: "Paramètres", stock: "Stock", order: "Commande", table_order: "Commande salle",
};

function relative(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
function fmtDayKey(iso) {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  const same = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (same(d, today)) return "Aujourd'hui";
  if (same(d, y)) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

const FILTERS = [
  { id: "all",    label: "Tout" },
  { id: "sale",   label: "Ventes" },
  { id: "create", label: "Créations" },
  { id: "update", label: "Modifs" },
  { id: "delete", label: "Suppressions" },
  { id: "restock",label: "Stock" },
  { id: "team_create,team_update,team_delete", label: "Équipe" },
];

export default function Activity() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [summary, setSummary] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [logRes, sumRes] = await Promise.all([
        axios.get(`${API}/activity?limit=200`, getAuthHeaders()),
        axios.get(`${API}/activity/summary`, getAuthHeaders()),
      ]);
      setItems(logRes.data?.items || []);
      setSummary(sumRes.data || null);
    } catch (e) {
      /* silent */
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    const allow = new Set(filter.split(","));
    return items.filter((i) => allow.has(i.action));
  }, [items, filter]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const key = fmtDayKey(it.created_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="activity-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Journal d'activité</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Suivez qui a fait quoi dans votre boutique — créations, modifications, ventes, suppressions.
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-4 h-10 rounded-xl bg-secondary text-foreground text-[13px] font-medium hover:bg-muted transition-colors"
          data-testid="activity-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {/* Summary tiles */}
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: "sale",    label: "Ventes",        Icon: ShoppingCart, color: "#F59E0B" },
            { key: "create",  label: "Créations",     Icon: PlusCircle,   color: "#22C55E" },
            { key: "update",  label: "Modifs",        Icon: Pencil,       color: "#3B82F6" },
            { key: "delete",  label: "Suppressions",  Icon: Trash2,       color: "#EF4444" },
          ].map(({ key, label, Icon, color }) => (
            <div key={key} className="p-4 rounded-2xl bg-card border border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                     style={{ backgroundColor: `${color}18`, color }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
                  <p className="text-xl font-bold text-foreground leading-tight">{summary.by_action?.[key] || 0}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`activity-filter-${f.id.split(',')[0]}`}
            className={`px-3.5 h-9 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
              filter === f.id
                ? "bg-foreground text-background"
                : "bg-secondary text-foreground hover:bg-muted"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : grouped.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <ShoppingBag className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-foreground font-semibold">Aucune activité pour l'instant</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            Dès qu'une action sera effectuée dans la boutique, elle apparaîtra ici.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([day, rows]) => (
            <section key={day} className="space-y-2">
              <h2 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground px-1">
                {day} <span className="text-muted-foreground/70">· {rows.length}</span>
              </h2>
              <div className="rounded-2xl bg-card border border-border divide-y divide-border">
                {rows.map((it) => <Row key={it.id} it={it} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ it }) {
  const meta = ACTION_META[it.action] || { label: it.action, Icon: Pencil, color: "#6B7280" };
  const Icon = meta.Icon;
  const entityLabel = ENTITY_LABEL[it.entity_type] || it.entity_type;
  const isTeam = it.actor_type === "team_member";

  const meta_parts = [];
  if (it.metadata?.total != null) meta_parts.push(`${Number(it.metadata.total).toFixed(2)} €`);
  if (it.metadata?.items_count != null) meta_parts.push(`${it.metadata.items_count} article${it.metadata.items_count > 1 ? "s" : ""}`);
  if (it.metadata?.quantity != null) meta_parts.push(`+${it.metadata.quantity} unités`);
  if (it.metadata?.variant) meta_parts.push(it.metadata.variant);
  if (it.metadata?.customer) meta_parts.push(it.metadata.customer);

  return (
    <div className="flex items-start gap-3 p-4" data-testid={`activity-row-${it.id}`}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
           style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[14px] text-foreground leading-tight">
              <span className="font-semibold">{meta.label}</span>
              {" · "}
              <span className="text-muted-foreground">{entityLabel}</span>
              {it.entity_label && (
                <>
                  {" "}
                  <span className="font-medium">« {it.entity_label} »</span>
                </>
              )}
            </p>
            <p className="text-[12px] text-muted-foreground mt-1 flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
              <span>par <span className="font-medium text-foreground/90">{it.actor_name}</span></span>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${isTeam ? "bg-purple-500/10 text-purple-500" : "bg-emerald-500/10 text-emerald-600"}`}>
                {isTeam ? (it.actor_role || "équipe") : "propriétaire"}
              </span>
              {meta_parts.length > 0 && (
                <>
                  <span>·</span>
                  <span>{meta_parts.join(" · ")}</span>
                </>
              )}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[12px] text-muted-foreground">{fmtTime(it.created_at)}</p>
            <p className="text-[10px] text-muted-foreground/70">{relative(it.created_at)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
